import React, { useState } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Select,
  Alert,
  AlertIcon,
  useToast,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiPlus, FiTrash2, FiDatabase, FiSearch } from 'react-icons/fi';
import { collectionsApi } from '../services/api';

const Collections = () => {
  const [newCollectionName, setNewCollectionName] = useState('');
  const [vectorSize] = useState(1536); // dimensão fixa (modelo de embeddings do sistema)
  const [selectedCollection, setSelectedCollection] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteCollection, setDeleteCollection] = useState<string | null>(null);
  
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure();
  
  const toast = useToast();
  const queryClient = useQueryClient();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  // Fetch collections
  const { data: collections = [], isLoading } = useQuery(
    'collections',
    collectionsApi.list,
    { refetchInterval: 10000 }
  );

  // Search results
  const [searchResults, setSearchResults] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);

  // Create collection mutation
  const createMutation = useMutation(
    ({ name, vectorSize }: { name: string; vectorSize: number }) =>
      collectionsApi.create(name, vectorSize),
    {
      onSuccess: (data) => {
        if (data.success) {
          toast({
            title: 'Base criada',
            description: data.message,
            status: 'success',
            duration: 5000,
          });
          queryClient.invalidateQueries('collections');
          onCreateClose();
          setNewCollectionName('');
        } else {
          toast({
            title: 'Erro ao criar collection',
            description: data.error || 'Erro desconhecido',
            status: 'error',
            duration: 5000,
          });
        }
      },
      onError: (error: any) => {
        toast({
          title: 'Erro ao criar collection',
          description: error.response?.data?.detail || 'Erro desconhecido',
          status: 'error',
          duration: 5000,
        });
      },
    }
  );

  // Delete collection mutation
  const deleteMutation = useMutation(
    (name: string) => collectionsApi.delete(name),
    {
      onSuccess: (data) => {
        if (data.success) {
          toast({
            title: 'Base excluída',
            description: data.message,
            status: 'success',
            duration: 5000,
          });
          queryClient.invalidateQueries('collections');
        } else {
          toast({
            title: 'Erro ao deletar collection',
            description: data.error || 'Erro desconhecido',
            status: 'error',
            duration: 5000,
          });
        }
        onDeleteClose();
        setDeleteCollection(null);
      },
      onError: (error: any) => {
        toast({
          title: 'Erro ao deletar collection',
          description: error.response?.data?.detail || 'Erro desconhecido',
          status: 'error',
          duration: 5000,
        });
        onDeleteClose();
        setDeleteCollection(null);
      },
    }
  );

  const handleCreateCollection = () => {
    if (!newCollectionName.trim()) {
      toast({
        title: 'Nome obrigatório',
        description: 'Informe um nome para a collection',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    createMutation.mutate({
      name: newCollectionName.trim(),
      vectorSize,
    });
  };

  const handleDeleteClick = (collectionName: string) => {
    setDeleteCollection(collectionName);
    onDeleteOpen();
  };

  const handleDeleteConfirm = () => {
    if (deleteCollection) {
      deleteMutation.mutate(deleteCollection);
    }
  };

  const handleSearch = async () => {
    if (!selectedCollection || !searchQuery.trim()) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Selecione uma collection e digite uma consulta',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSearching(true);
    try {
      const results = await collectionsApi.search(selectedCollection, searchQuery.trim(), 10);
      setSearchResults(results);
    } catch (error: any) {
      toast({
        title: 'Erro na busca',
        description: error.response?.data?.detail || 'Erro desconhecido',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSearching(false);
    }
  };

  if (isLoading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="brand.500" />
        <Text mt={4}>Carregando bases...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <VStack spacing={8} align="stretch">
        <HStack justify="space-between" align="center">
          <Box>
            <Heading size="lg">Bases de Conhecimento</Heading>
            <Text color="gray.600">
              Organize seus documentos em bases para buscar e conversar com a IA
            </Text>
          </Box>
          <HStack spacing={4}>
            <Button
              leftIcon={<FiSearch />}
              colorScheme="blue"
              variant="outline"
              onClick={onSearchOpen}
              isDisabled={collections.length === 0}
            >
              Buscar
            </Button>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="brand"
              onClick={onCreateOpen}
            >
              Nova base
            </Button>
          </HStack>
        </HStack>

        {collections.length === 0 ? (
          <Card>
            <CardBody textAlign="center" py={12}>
              <VStack spacing={4}>
                <Flex bg="brand.50" boxSize="72px" borderRadius="full" align="center" justify="center">
                  <FiDatabase size={34} color="#6B46C1" />
                </Flex>
                <Box>
                  <Heading size="md" color="gray.700">
                    Crie sua primeira base de conhecimento
                  </Heading>
                  <Text color="gray.500" mt={1} maxW="md" mx="auto">
                    Uma base reúne seus documentos para você buscar e conversar com a IA.
                    Depois você alimenta por upload ou integrações.
                  </Text>
                </Box>
                <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={onCreateOpen}>
                  Criar base
                </Button>
              </VStack>
            </CardBody>
          </Card>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {collections.map((collection) => (
              <CollectionCard
                key={collection.name}
                collection={collection}
                onDelete={() => handleDeleteClick(collection.name)}
              />
            ))}
          </SimpleGrid>
        )}

        {/* Create Collection Modal */}
        <Modal isOpen={isCreateOpen} onClose={onCreateClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Nova base</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Nome da base</FormLabel>
                  <Input
                    placeholder="ex: juridico, comercial, contratos"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                  />
                </FormControl>
                
                <Alert status="info" size="sm">
                  <AlertIcon />
                  <Text fontSize="sm">
                    O tamanho do vetor deve corresponder ao modelo de embeddings que você planeja usar.
                  </Text>
                </Alert>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onCreateClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="brand"
                onClick={handleCreateCollection}
                isLoading={createMutation.isLoading}
              >
                Criar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Search Modal */}
        <Modal isOpen={isSearchOpen} onClose={onSearchClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Buscar na base</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Base de conhecimento</FormLabel>
                  <Select
                    placeholder="Selecione uma base"
                    value={selectedCollection}
                    onChange={(e) => setSelectedCollection(e.target.value)}
                  >
                    {collections.map((collection) => (
                      <option key={collection.name} value={collection.name}>
                        {collection.name} ({collection.vectors_count} vetores)
                      </option>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl>
                  <FormLabel>Consulta</FormLabel>
                  <Input
                    placeholder="Digite sua pergunta ou termo de busca"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </FormControl>
                
                <Button
                  colorScheme="brand"
                  onClick={handleSearch}
                  isLoading={isSearching}
                  isDisabled={!selectedCollection || !searchQuery.trim()}
                  w="full"
                >
                  Buscar
                </Button>
                
                {searchResults && (
                  <Box w="full" mt={4}>
                    <Text fontWeight="semibold" mb={2}>
                      Resultados ({searchResults.total_results}):
                    </Text>
                    <VStack spacing={3} align="stretch" maxH="300px" overflowY="auto">
                      {searchResults.results.map((result: any, index: number) => (
                        <Box key={index} p={3} bg="gray.50" borderRadius="md" fontSize="sm">
                          <HStack justify="space-between" mb={1}>
                            <Text fontWeight="semibold">{result.filename}</Text>
                            <Text color="gray.500">Score: {result.score.toFixed(3)}</Text>
                          </HStack>
                          <Text noOfLines={3}>{result.text}</Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onSearchClose}>Fechar</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Delete Confirmation */}
        <AlertDialog
          isOpen={isDeleteOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeleteClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Deletar Collection
              </AlertDialogHeader>
              <AlertDialogBody>
                Tem certeza que deseja deletar a collection "{deleteCollection}"?
                Esta ação não pode ser desfeita e todos os vetores serão perdidos.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteClose}>
                  Cancelar
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleDeleteConfirm}
                  ml={3}
                  isLoading={deleteMutation.isLoading}
                >
                  Deletar
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </VStack>
    </Box>
  );
};

// Individual collection card component
const CollectionCard = ({ 
  collection, 
  onDelete 
}: { 
  collection: any; 
  onDelete: () => void; 
}) => {
  const { data: stats, isLoading } = useQuery(
    ['collection-stats', collection.name],
    () => collectionsApi.getStats(collection.name),
    { refetchInterval: 30000 }
  );

  return (
    <Card>
      <CardHeader>
        <HStack justify="space-between">
          <VStack align="start" spacing={1}>
            <Heading size="md">{collection.name}</Heading>
            <Text fontSize="sm" color="gray.500">
              {stats?.status || 'unknown'} status
            </Text>
          </VStack>
          <IconButton
            aria-label="Deletar collection"
            icon={<FiTrash2 />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={onDelete}
          />
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        <SimpleGrid columns={2} spacing={4}>
          <Stat>
            <StatLabel>Vetores</StatLabel>
            <StatNumber fontSize="lg">
              {isLoading ? <Spinner size="sm" /> : (stats?.total_vectors || 0)}
            </StatNumber>
            <StatHelpText fontSize="xs">Total de trechos indexados</StatHelpText>
          </Stat>
          
          <Stat>
            <StatLabel>Pontos</StatLabel>
            <StatNumber fontSize="lg">
              {isLoading ? <Spinner size="sm" /> : (stats?.total_points || 0)}
            </StatNumber>
            <StatHelpText fontSize="xs">Chunks de documentos</StatHelpText>
          </Stat>
        </SimpleGrid>
      </CardBody>
    </Card>
  );
};

export default Collections;